import { useEffect, useState } from 'react';
import { MdDelete } from 'react-icons/md';
import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import TableColumn from '../../components/TableColumn/TableColumn';
import api from '../../services/api';
import { toast } from 'react-toastify';

const ALL_SIX = ['guestlist', 'bottleService', 'ticketSales', 'offers', 'withdrawalRequests', 'followers'];
const PERM_LABEL = {
  guestlist: 'Guest Lists',
  bottleService: 'Bottle Service',
  ticketSales: 'Tickets',
  offers: 'Promotions',
  withdrawalRequests: 'Withdrawals',
  followers: 'Followers',
};

function formatPermissions(perms) {
  if (!perms || perms.length === 0) return 'Full access';
  if (perms.includes('all')) return 'Full access';
  if (ALL_SIX.every(p => perms.includes(p))) return 'Full access';
  return perms.map(p => PERM_LABEL[p] || p).join(', ');
}

function MyOrganizer() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [allVenues, setAllVenues] = useState([]);
  const [selectedVenueIds, setSelectedVenueIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null); // { requestId, venueId, userName, venueName }
  const [removing, setRemoving] = useState(false);

  const loadList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/organizer-requests/mine');
      setCurrentUserId(data?.current_user_id || null);
      const flattened = (data?.data || []).flatMap(r =>
        (r.venues || []).map(v => ({
          User: r.user?.name || '—',
          Email: r.user?.email || '',
          Venue: v.name,
          Permissions: formatPermissions(r.permissions),
          Added: r.created_at ? new Date(r.created_at).toLocaleDateString() : '—',
          _id: `${r._id}-${v.id}`,
          // Stash raw ids so the remove action can fire without re-parsing.
          _requestId: r._id,
          _venueId: v.id,
          _userName: r.user?.name || '',
          _venueName: v.name,
        }))
      ).sort((a, b) => {
        if (a.User !== b.User) return a.User.localeCompare(b.User);
        return a.Venue.localeCompare(b.Venue);
      });
      setRows(flattened);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await api.delete(`/organizer-requests/my-roles/${removeTarget.requestId}/venue/${removeTarget.venueId}`);
      toast.success(`Removed ${removeTarget.userName} from ${removeTarget.venueName}.`);
      setRemoveTarget(null);
      await loadList();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Failed to remove.');
    } finally {
      setRemoving(false);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  const openAddModal = async () => {
    setShowAddModal(true);
    if (allVenues.length === 0) {
      try {
        const { data } = await api.get('/venues', { params: { records: 9999 } });
        const list = (data?.data || data || []).map(v => ({ id: v._id || v.id, name: v.name })).sort((a, b) => a.name.localeCompare(b.name));
        setAllVenues(list);
      } catch (err) {
        console.error('Failed to load venues:', err);
        toast.error('Could not load venues.');
      }
    }
  };

  const submitAdd = async () => {
    if (!currentUserId) { toast.error('Cannot identify your user. Reload and try again.'); return; }
    if (selectedVenueIds.size === 0) { toast.error('Pick at least one venue.'); return; }
    setSubmitting(true);
    try {
      await api.post('/organizer-requests/add-organizer', {
        // Empty array = full/unscoped organizer access (the SideBar +
        // usePermissions contract; the server unions all rows' permissions,
        // and a non-empty array scopes you to only those keys). Sending
        // ['all'] poisoned that union, since 'all' is not a real permission
        // key, and silently stripped guestlist/bottle/ticket access across
        // every venue. Self-add for an internal moderator = full access.
        selectedUserId: currentUserId,
        permissions: [],
        venues: Array.from(selectedVenueIds),
      });
      toast.success('Added to venue(s).');
      setShowAddModal(false);
      setSelectedVenueIds(new Set());
      await loadList();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Failed to add.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleVenue = (id) => {
    setSelectedVenueIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article>
          <section className="moderators">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 24 }}>
              <div>
                <h1 style={{ fontSize: 28, fontWeight: 700, color: '#fff', margin: '0 0 8px 0' }}>My Organizer Roles</h1>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14, margin: 0 }}>
                  Every Zona-internal team member's organizer assignments. SMS routing fires to organizers with bottle service permission.
                </p>
              </div>
              <button
                onClick={openAddModal}
                style={{
                  background: '#4F30EA', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                + Add me to a venue
              </button>
            </div>
            <TableColumn
              title=""
              columns={['User', 'Email', 'Venue', 'Permissions', 'Added']}
              data={rows}
              hasExport={false}
              hasSearch={false}
              isLoading={loading}
              hasHeader={false}
              actions={[
                {
                  label: <MdDelete size={26} color="#ff5a5f" title="Remove from this venue" />,
                  onClick: (rowData) => setRemoveTarget({
                    requestId: rowData._requestId,
                    venueId: rowData._venueId,
                    userName: rowData._userName,
                    venueName: rowData._venueName,
                  }),
                },
              ]}
            />
          </section>

          {showAddModal && (
            <div
              onClick={() => setShowAddModal(false)}
              style={{
                position: 'fixed', inset: 0, zIndex: 1000,
                background: 'rgba(0,0,0,0.6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 20,
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: '#1a1a1d', borderRadius: 12,
                  width: '100%', maxWidth: 520, maxHeight: '80vh',
                  display: 'flex', flexDirection: 'column',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <div style={{ padding: 20, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <h2 style={{ color: '#fff', margin: 0, fontSize: 18, fontWeight: 600 }}>Add me to a venue</h2>
                  <p style={{ color: 'rgba(255,255,255,0.4)', margin: '6px 0 0', fontSize: 12 }}>
                    Pick the venues to assign yourself as organizer with full permissions.
                  </p>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '8px 8px' }}>
                  {allVenues.length === 0 ? (
                    <div style={{ color: 'rgba(255,255,255,0.4)', padding: 20, textAlign: 'center' }}>Loading venues…</div>
                  ) : (
                    allVenues.map((v) => {
                      const checked = selectedVenueIds.has(v.id);
                      return (
                        <label
                          key={v.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '10px 12px', borderRadius: 6,
                            cursor: 'pointer',
                            background: checked ? 'rgba(79, 48, 234, 0.12)' : 'transparent',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleVenue(v.id)}
                            style={{ accentColor: '#4F30EA' }}
                          />
                          <span style={{ color: '#fff', fontSize: 14 }}>{v.name}</span>
                        </label>
                      );
                    })
                  )}
                </div>
                <div style={{
                  padding: 16, borderTop: '1px solid rgba(255,255,255,0.06)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                }}>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
                    {selectedVenueIds.size} selected
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => { setShowAddModal(false); setSelectedVenueIds(new Set()); }}
                      style={{
                        background: 'transparent', color: 'rgba(255,255,255,0.7)',
                        border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6,
                        padding: '8px 14px', fontSize: 13, cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={submitAdd}
                      disabled={submitting || selectedVenueIds.size === 0}
                      style={{
                        background: submitting || selectedVenueIds.size === 0 ? 'rgba(79, 48, 234, 0.4)' : '#4F30EA',
                        color: '#fff', border: 'none', borderRadius: 6,
                        padding: '8px 14px', fontSize: 13, fontWeight: 600,
                        cursor: submitting || selectedVenueIds.size === 0 ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {submitting ? 'Adding…' : 'Add'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {removeTarget && (
            <div
              onClick={() => !removing && setRemoveTarget(null)}
              style={{
                position: 'fixed', inset: 0, zIndex: 1100,
                background: 'rgba(0,0,0,0.6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 20,
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: '#1a1a1d', borderRadius: 12,
                  width: '100%', maxWidth: 460, padding: 24,
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <h2 style={{ color: '#fff', margin: 0, fontSize: 18, fontWeight: 600 }}>
                  Remove organizer assignment?
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.6)', margin: '12px 0 4px', fontSize: 14, lineHeight: 1.5 }}>
                  This will remove <strong style={{ color: '#fff' }}>{removeTarget.userName}</strong> from <strong style={{ color: '#fff' }}>{removeTarget.venueName}</strong>.
                </p>
                <p style={{ color: 'rgba(255,255,255,0.4)', margin: '8px 0 20px', fontSize: 12 }}>
                  Other venues this person organizes are not affected. No email is sent.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button
                    onClick={() => setRemoveTarget(null)}
                    disabled={removing}
                    style={{
                      background: 'transparent', color: 'rgba(255,255,255,0.7)',
                      border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6,
                      padding: '8px 14px', fontSize: 13,
                      cursor: removing ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmRemove}
                    disabled={removing}
                    style={{
                      background: removing ? 'rgba(255, 90, 95, 0.4)' : '#ff5a5f',
                      color: '#fff', border: 'none', borderRadius: 6,
                      padding: '8px 14px', fontSize: 13, fontWeight: 600,
                      cursor: removing ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {removing ? 'Removing…' : 'Remove'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </article>
      </div>
    </main>
  );
}

export default MyOrganizer;
